import React from 'react';
import { Card, Typography } from 'antd';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { DollarOutlined } from '@ant-design/icons';
import { MonthlyRevenue } from '../../../../types/super-admin.types';
import moment from 'moment';

const { Title } = Typography;

interface RevenueChartProps {
    data: MonthlyRevenue[];
    loading?: boolean;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ data, loading = false }) => {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formattedData = data.map(item => ({
      ...item,
      month: moment(item.month, 'YYYY-MM').format('MMM'),
    }));

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border rounded shadow-sm">
                    <p className="fw-bold mb-2">{`${label}`}</p>
                    <p className="text-primary mb-1">
                        <span className="fw-bold">Gelir: </span>
                        {formatCurrency(payload[0].value)}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <Card 
            className="border-0 shadow-sm"
            loading={loading}
            title={
                <div className="d-flex align-items-center">
                    <div className="me-2 p-2 bg-primary bg-opacity-10 rounded">
                        <DollarOutlined className="text-primary" />
                    </div>
                    <div>
                        <Title level={5} className="mb-0">Aylık Gelir Trendi</Title>
                        <small className="text-muted">Son 12 ayın gelir analizi</small>
                    </div>
                </div>
            }
            bodyStyle={{ padding: '20px' }}
        >
            <div style={{ height: '350px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={formattedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                        <defs>
                            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#667eea" stopOpacity={0.1}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                            dataKey="month" 
                            stroke="#666"
                            fontSize={12}
                            tickLine={false}
                        />
                        <YAxis 
                            stroke="#667eea"
                            fontSize={12}
                            tickLine={false}
                            tickFormatter={(value) => `₺${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke="#667eea"
                            strokeWidth={3}
                            fill="url(#revenueGradient)"
                            dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: '#667eea', strokeWidth: 2 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-3 pt-3 border-top">
                <div className="row text-center">
                    <div className="col-12">
                        <small className="text-muted">
                            Toplam Gelir: {formatCurrency(data.reduce((sum, item) => sum + item.revenue, 0))}
                        </small>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default RevenueChart; 